import { useCallback } from 'react'
import { buildErrorMessage, withCommentFilterPlan } from '@constants'
import { getModelXyzBounds } from '@domainUtils'
import { AxiosError } from 'axios'
import useGetPlanSettings from '@editor/hooks/requests/useGetPlanSettings'
import usePlanTableEditMode from '@editor/hooks/usePlanTableEditMode'
import usePlanTableMutations from '@editor/hooks/usePlanTableMutations'
import { ADD_PLAN_TABLES_SELECTION_KEY } from '@editor/hooks/usePlanTableTool'
import { getPlanViewStorey } from '@editor/pages/PlanView/utils'
import {
  DEFAULT_PLAN_TABLE_NAME,
  defaultPlanConfigForTemplate,
  defaultTemplateOptionsForTemplate,
} from '@editor/stores/planTableStore/utils'
import { useCameraStore } from '@modugen/ui-toolkit'
import { useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import {
  useCommentFilterStore,
  useControlStore,
  useModelStore,
  usePlanTableStore,
} from '@editorStores'
import { useSelectionMode } from '@editorHooks'
import { getPlanTableTemplates } from '@queries'
import { createDefaultPlanTableData } from 'src/domain/planTables/defaults'
import type { PlanTablePositionAnchor } from 'src/domain/planTables/positionAnchor'
import type { PlanTable, PlanTableTemplate } from 'src/domain/planTables/types'
import { usePersistentParams } from 'src/hooks'
import { computeAutoPlanTablePosition } from './utils'

/**
 * Creates a blank (`templateKey === null`) or template plan table at a given position, with the
 * plan config, name and template options a new table gets by default. `quickAddPlanTable` places
 * it automatically (see `computeAutoPlanTablePosition`) and selects it in the scene.
 */
const useCreatePlanTable = () => {
  const selectedPlan = useCommentFilterStore(state => state.selectedPlan)
  const floorplanStorey = useControlStore(state => state.floorplanState.storey)
  const availableStoreys = useModelStore(state => state.availableStoreys)
  const visibleStoreys = useModelStore(state => state.visibleStoreys)
  const isOrthographic = useCameraStore(state => state.isOrthographic)
  const setIsVisible = usePlanTableStore(state => state.setIsVisible)
  const setIsActive = usePlanTableStore(state => state.setIsActive)
  const setPendingTemplateKey = usePlanTableStore(state => state.setPendingTemplateKey)
  const { createPlanTableFromInput } = usePlanTableMutations()
  const { selectPlanTableInScene } = usePlanTableEditMode()
  const { selectionModeKey, unsetSelectionMode } = useSelectionMode()
  const { data: planSettings } = useGetPlanSettings()
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()
  const { projectId }: { projectId?: string } = usePersistentParams()

  const createPlanTable = useCallback(
    async (
      templateKey: string | null,
      position: PlanTable['position'],
      positionAnchor: PlanTablePositionAnchor = 'top-left',
    ): Promise<PlanTable | null> => {
      const planStorey =
        isOrthographic && floorplanStorey
          ? getPlanViewStorey(floorplanStorey, visibleStoreys)
          : null
      // Flag the table for the filtered plan, or that filter (e.g. the plan view's) hides it.
      const planConfig = withCommentFilterPlan(
        defaultPlanConfigForTemplate(templateKey, availableStoreys),
        selectedPlan,
        planStorey,
      )

      const { isActive } = usePlanTableStore.getState()

      let tableName = DEFAULT_PLAN_TABLE_NAME
      if (templateKey) {
        let templates: PlanTableTemplate[]
        try {
          templates = await queryClient.fetchQuery({
            queryKey: getPlanTableTemplates.getKey(projectId),
            queryFn: () => getPlanTableTemplates.request(projectId),
          })
        } catch (error) {
          enqueueSnackbar(
            buildErrorMessage(error as AxiosError, 'Fehler beim Laden der Tabellenvorlagen'),
            { variant: 'error' },
          )
          return null
        }
        tableName =
          templates.find((template: PlanTableTemplate) => template.key === templateKey)
            ?.defaultName ?? DEFAULT_PLAN_TABLE_NAME
      }

      return createPlanTableFromInput(
        templateKey
          ? {
              name: tableName,
              position,
              positionAnchor,
              tableType: 'TemplatePlanTable',
              templateKey,
              planConfig,
              templateOptions: defaultTemplateOptionsForTemplate(templateKey),
              isVisible: true,
              isActive,
            }
          : {
              name: tableName,
              position,
              positionAnchor,
              tableType: 'CustomPlanTable',
              table: createDefaultPlanTableData(),
              planConfig,
              isVisible: true,
              isActive,
            },
      )
    },
    [
      availableStoreys,
      createPlanTableFromInput,
      floorplanStorey,
      isOrthographic,
      enqueueSnackbar,
      projectId,
      queryClient,
      selectedPlan,
      visibleStoreys,
    ],
  )

  const quickAddPlanTable = useCallback(
    async (templateKey: string | null) => {
      if (selectionModeKey === ADD_PLAN_TABLES_SELECTION_KEY) {
        setPendingTemplateKey(null)
        unsetSelectionMode()
      }
      setIsActive(true)
      setIsVisible(true)

      const { model } = useModelStore.getState()
      const modelBounds = getModelXyzBounds(model)
      const storeyLowerZ =
        isOrthographic && floorplanStorey ? model.storey_boundaries[floorplanStorey]?.[0] : null
      const z = typeof storeyLowerZ === 'number' ? storeyLowerZ : modelBounds.zMin

      const { tables, activeFilterViewId } = usePlanTableStore.getState()
      const existingPositions = tables
        .filter(table => table.filterViewId === activeFilterViewId)
        .map(table => table.position)

      const position = computeAutoPlanTablePosition({
        planSettings,
        modelBounds,
        existingPositions,
        z,
      })

      // Failures are surfaced by the template fetch's and the create mutation's error snackbars.
      const created = await createPlanTable(templateKey, position, 'top-left').catch(() => null)
      if (created) selectPlanTableInScene(created.id)
    },
    [
      createPlanTable,
      floorplanStorey,
      isOrthographic,
      planSettings,
      selectPlanTableInScene,
      selectionModeKey,
      setIsActive,
      setIsVisible,
      setPendingTemplateKey,
      unsetSelectionMode,
    ],
  )

  return { createPlanTable, quickAddPlanTable }
}

export default useCreatePlanTable
